import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Text,
} from "@chakra-ui/react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button, IconButton } from "@chakra-ui/button";
import { Box, Divider, HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { isEmptyStr } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import * as Yup from "yup";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { ApiResult } from "../api/dataApiClient";
import { PlusIcon } from "../styles/icons";
import { ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import DateUtil from "../util/DateUtil";
import { v4 as uuid } from "uuid";
import {
  chakraComponents,
  OptionBase,
  GroupBase,
  OptionProps,
} from "chakra-react-select";

interface ApplicationDetailFormValues {
  clientId?: string | null;
  clientSecrets?: CherryHubAdminApi.ClientSecret[] | null;
  defaultPermissions?: string[] | null;
  displayName?: string | null;
  homePageUrl?: string | null;
  isEnabled?: boolean;
  logoUrl?: string | null;
  privacyStatementUrl?: string | null;
  publisherBusinessName?: string | null;
  publisherDomain?: string | null;
  publisherPrimaryContactName?: string | null;
  publisherPrimaryContactTelephone?: string | null;
  publisherPrimaryContactEmailAddress?: string | null;
  publisherSecondaryContactName?: string | null;
  publisherSecondaryContactTelephone?: string | null;
  publisherSecondaryContactEmailAddress?: string | null;
  redirectUris?: string[] | null;
  requireClientSecretOnOAuth2TokenRequest?: boolean;
  returnExistingUserOnOAuth2AuthorizationCodeFlow?: boolean;
  supportEmailAddress?: string | null;
  supportUrl?: string | null;
  termsOfServiceUrl?: string | null;
}

interface ApplicationDetailFormProps {
  apiClient: AdminApiClient;
  application: CherryHubAdminApi.PlatformApplication | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (application: CherryHubAdminApi.PlatformApplication) => void;
  onFailure: (message?: string) => void;
}

interface PermissionOption extends OptionBase {
  label: string;
  value: string;
  displayName: string;
  description: string;
}

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  values,
  application,
  setFieldValue,
}: ApplicationDetailFormProps & FormikProps<ApplicationDetailFormValues>) => {
  const [permissionsOptions, setPermissionOptions] = useState<
    {
      label: string;
      value: string;
      description: string;
      displayName: string;
    }[]
  >([]);
  const { data: permissions, isLoading: permissionOptionsLoading } =
    useAdminApiRequest(
      (apiClient) => apiClient.getApplicationPermissions(),
      []
    );

  useEffect(() => {
    const options =
      permissions?.map((venue) => {
        return {
          label: venue.Value!,
          value: venue.Meta!.Id!,
          description: venue.Description!,
          displayName: venue.DisplayName!,
        };
      }) || [];
    options.sort((a, b) => {
      if (a.value < b.value) return -1;
      if (a.value > b.value) return 1;
      return 0;
    });
    setPermissionOptions(options);
  }, [permissions]);

  const customComponents = {
    Option: ({ children, ...props }: OptionProps<any, any, GroupBase<any>>) => (
      <chakraComponents.Option {...props}>
        <VStack w="100%" alignItems="flex-start" spacing={0}>
          <div>
            <strong>{props.data.value}</strong> - {props.data.displayName}
          </div>
          <i>{props.data.description}</i>
          <Divider pt={2} />
        </VStack>
      </chakraComponents.Option>
    ),
  };

  return (
    <>
      <Box width="100%" alignSelf="flex-start">
        <FormStack>
          {application && (
            <HStack w="100%">
              <Box flexGrow={2} pb={4}>
                <Text fontSize="md" color="cherryUi.600">
                  {application?.id}
                </Text>
                <Text fontSize="xl" color="cherryUi.600">
                  <b>{application?.DisplayName}</b>
                </Text>
              </Box>

              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button disabled={isSubmitting} onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </HStack>
          )}
          <VStack
            w="100%"
            maxW="1200px"
            alignItems="flex-start"
            pb={10}
            spacing={4}
          >
            <TextField
              horizontal
              label="Display Name"
              name="displayName"
              valueProps={{ flex: 2 }}
            />
            <SwitchField
              horizontal
              label="Enabled"
              name="isEnabled"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Logo Url"
              name="logoUrl"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Home Page Url"
              name="homePageUrl"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Terms Of Service Url"
              name="termsOfServiceUrl"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Privacy Statement Url"
              name="privacyStatementUrl"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Support Url"
              name="supportUrl"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Domain"
              name="publisherDomain"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Business Name"
              name="publisherBusinessName"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Support Email Address"
              name="supportEmailAddress"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Primary Contact Name"
              name="publisherPrimaryContactName"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Primary Contact Telephone"
              name="publisherPrimaryContactTelephone"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Primary Contact Email Address"
              name="publisherPrimaryContactEmailAddress"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Secondary Contact Name"
              name="publisherSecondaryContactName"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Secondary Contact Telephone"
              name="publisherSecondaryContactTelephone"
              valueProps={{ flex: 2 }}
            />
            <TextField
              horizontal
              label="Publisher Secondary Contact Email Address"
              name="publisherSecondaryContactEmailAddress"
              valueProps={{ flex: 2 }}
            />

            <ChakraReactSelectField
              horizontal
              label="Default Permissions"
              name="defaultPermissions"
              selectedOptionColor="blue"
              isMulti
              closeMenuOnSelect={false}
              isLoading={permissionOptionsLoading}
              options={permissionsOptions}
              components={customComponents}
              valueProps={{ flex: 2 }}
            />

            <ChakraReactSelectField
              horizontal
              label="Allowed Permissions"
              name="allowedPermissions"
              selectedOptionColor="blue"
              isMulti
              closeMenuOnSelect={false}
              isLoading={permissionOptionsLoading}
              options={permissionsOptions}
              components={customComponents}
              valueProps={{ flex: 2 }}
            />

            <SwitchField
              horizontal
              label="Return Existing User On OAuth2 Authorization Code Flow"
              name="returnExistingUserOnOAuth2AuthorizationCodeFlow"
              valueProps={{ flex: 2 }}
            />
          </VStack>
          <Accordion
            w="100%"
            allowToggle
            allowMultiple
            variant="section"
            defaultIndex={[0, 1]}
          >
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <b>Redirect Uris</b>
                      </Box>
                      {isExpanded ? (
                        <ChevronUpIcon color="white" fontSize="xl" />
                      ) : (
                        <AccordionIcon />
                      )}
                    </AccordionButton>
                  </h2>
                  <AccordionPanel width="100%">
                    <VStack w="100%" spacing={4} alignItems="flex-start">
                      <Box
                        w="100%"
                        p={4}
                        border="1px solid"
                        borderColor="cherryButtonDark.50"
                        borderRadius="md"
                      >
                        <HStack w="100%">
                          <Text flexGrow={2}>
                            <b>Redirect Uris</b> :
                          </Text>
                          <Button
                            size="xs"
                            leftIcon={<PlusIcon />}
                            onClick={() => {
                              if (values.redirectUris) {
                                values.redirectUris?.push("");
                                setFieldValue(
                                  "redirectUris",
                                  values.redirectUris
                                );
                              } else {
                                setFieldValue("redirectUris", [""]);
                              }
                            }}
                            colorScheme="cherryButton"
                          >
                            New Redirect Uri
                          </Button>
                        </HStack>

                        <Divider mb={2} mt={1} />
                        <VStack w="100%" spacing={4}>
                          {(values.redirectUris?.length &&
                            values.redirectUris?.map((uri, uriIdx) => {
                              return (
                                <HStack w="100%" key={uriIdx}>
                                  <TextField
                                    label={`Uri ${uriIdx + 1}`}
                                    name={`redirectUris[${uriIdx}]`}
                                  />
                                  <Box h="100%" pb={1} alignSelf="flex-end">
                                    <IconButton
                                      aria-label={`Delete redirect uri.`}
                                      title={`Delete redirect uri.`}
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      onClick={() => {
                                        values.redirectUris?.splice(uriIdx, 1);
                                        setFieldValue(
                                          "redirectUris",
                                          values.redirectUris
                                        );
                                      }}
                                    />
                                  </Box>
                                </HStack>
                              );
                            })) || (
                            <Text fontStyle="italic">
                              Redirect uris has not been set.
                            </Text>
                          )}
                        </VStack>
                      </Box>
                    </VStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <b>Client Secret</b>
                      </Box>
                      {isExpanded ? (
                        <ChevronUpIcon color="white" fontSize="xl" />
                      ) : (
                        <AccordionIcon />
                      )}
                    </AccordionButton>
                  </h2>
                  <AccordionPanel width="100%">
                    <VStack w="100%" spacing={4} alignItems="flex-start">
                      <SwitchField
                        horizontal
                        label="Require Client Secret On OAuth2 Token Request"
                        name="requireClientSecretOnOAuth2TokenRequest"
                        valueProps={{ flex: 2 }}
                      />
                      {values?.requireClientSecretOnOAuth2TokenRequest && (
                        <Box
                          w="100%"
                          p={4}
                          border="1px solid"
                          borderColor="cherryButtonDark.50"
                          borderRadius="md"
                        >
                          <HStack w="100%">
                            <Text flexGrow={2}>
                              <b>Client Secrets</b> :
                            </Text>
                            <Button
                              size="xs"
                              leftIcon={<PlusIcon />}
                              onClick={() => {
                                const identifier = uuid().replace(/-/g, "");
                                if (values.clientSecrets) {
                                  values.clientSecrets?.push({
                                    SecretId: identifier,
                                  });
                                  setFieldValue(
                                    "clientSecrets",
                                    values.clientSecrets
                                  );
                                } else {
                                  setFieldValue("clientSecrets", [
                                    {
                                      SecretId: identifier,
                                    },
                                  ]);
                                }
                              }}
                              colorScheme="cherryButton"
                            >
                              New Client Secret
                            </Button>
                          </HStack>

                          <Divider mb={2} mt={1} />
                          <VStack w="100%" spacing={4}>
                            {(values.clientSecrets?.length &&
                              values.clientSecrets?.map((secret, secretIdx) => {
                                return (
                                  <HStack w="100%" key={secretIdx}>
                                    <TextField
                                      label={`Secret Id ${secretIdx + 1}`}
                                      name={`clientSecrets[${secretIdx}].SecretId`}
                                      isDisabled={true}
                                    />
                                    <TextField
                                      label={`Expiry Date ${secretIdx + 1}`}
                                      name={`clientSecrets[${secretIdx}].ExpiryDate`}
                                      type="date"
                                    />
                                    <Box h="100%" pb={1} alignSelf="flex-end">
                                      <IconButton
                                        aria-label={`Delete client secret.`}
                                        title={`Delete client secret.`}
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        onClick={() => {
                                          values.clientSecrets?.splice(
                                            secretIdx,
                                            1
                                          );
                                          setFieldValue(
                                            "clientSecrets",
                                            values.clientSecrets
                                          );
                                        }}
                                      />
                                    </Box>
                                  </HStack>
                                );
                              })) || (
                              <Text fontStyle="italic">
                                Client secret has not been set.
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>

          <HStack width="100%" justifyContent="center" spacing="3" pt="8">
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const ApplicationDetailForm = withFormik<
  ApplicationDetailFormProps,
  ApplicationDetailFormValues
>({
  mapPropsToValues: ({ application }) => {
    return {
      clientSecrets: application?.ClientSecrets?.map((secret) => {
        return {
          SecretId: secret.SecretId,
          ExpiryDate: DateUtil.formatDate(
            DateUtil.getLocalDateFormatFromString(secret.ExpiryDate!),
            "en-ca",
            {
              year: "numeric",
              month: "2-digit",
              day: "numeric",
            }
          ),
        };
      }),
      defaultPermissions: application?.DefaultPermissions,
      displayName: application?.DisplayName,
      homePageUrl: application?.HomePageUrl,
      isEnabled: application?.IsEnabled,
      logoUrl: application?.LogoUrl,
      privacyStatementUrl: application?.PrivacyStatementUrl,
      publisherBusinessName: application?.PublisherBusinessName,
      publisherDomain: application?.PublisherDomain,
      publisherPrimaryContactName: application?.PublisherPrimaryContactName,
      publisherPrimaryContactTelephone:
        application?.PublisherPrimaryContactTelephone,
      publisherPrimaryContactEmailAddress:
        application?.PublisherPrimaryContactEmailAddress,
      publisherSecondaryContactName: application?.PublisherSecondaryContactName,
      publisherSecondaryContactTelephone:
        application?.PublisherSecondaryContactTelephone,
      publisherSecondaryContactEmailAddress:
        application?.PublisherSecondaryContactEmailAddress,
      redirectUris: application?.RedirectUris,
      requireClientSecretOnOAuth2TokenRequest:
        application?.RequireClientSecretOnOAuth2TokenRequest,
      returnExistingUserOnOAuth2AuthorizationCodeFlow:
        application?.ReturnExistingUserOnOAuth2AuthorizationCodeFlow,
      supportEmailAddress: application?.SupportEmailAddress,
      supportUrl: application?.SupportUrl,
      termsOfServiceUrl: application?.TermsOfServiceUrl,
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    if (isEmptyStr(values.displayName)) {
      setFieldError("displayName", "Display name is required.");
      return;
    }

    // Translate form state to IntegrationUpdate
    const applicationDoc: Partial<CherryHubAdminApi.PlatformApplicationUpdate> =
      {
        ClientSecrets: values.clientSecrets?.map((secret) => {
          secret.ExpiryDate = DateUtil.convertToAtomDateTime(
            new Date(secret.ExpiryDate!)
          );
          return secret;
        }),
        DefaultPermissions: values.defaultPermissions,
        DisplayName: values.displayName,
        HomePageUrl: values.homePageUrl,
        IsEnabled: values.isEnabled,
        LogoUrl: values.logoUrl,
        PrivacyStatementUrl: values.privacyStatementUrl,
        PublisherBusinessName: values.publisherBusinessName,
        PublisherDomain: values.publisherDomain,
        PublisherPrimaryContactName: values.publisherPrimaryContactName,
        PublisherPrimaryContactTelephone:
          values.publisherPrimaryContactTelephone,
        PublisherPrimaryContactEmailAddress:
          values.publisherPrimaryContactEmailAddress,
        PublisherSecondaryContactName: values.publisherSecondaryContactName,
        PublisherSecondaryContactTelephone:
          values.publisherSecondaryContactTelephone,
        PublisherSecondaryContactEmailAddress:
          values.publisherSecondaryContactEmailAddress,
        RedirectUris: values.redirectUris,
        RequireClientSecretOnOAuth2TokenRequest:
          values.requireClientSecretOnOAuth2TokenRequest,
        ReturnExistingUserOnOAuth2AuthorizationCodeFlow:
          values.returnExistingUserOnOAuth2AuthorizationCodeFlow,
        SupportEmailAddress: values.supportEmailAddress,
        SupportUrl: values.supportUrl,
        TermsOfServiceUrl: values.termsOfServiceUrl,
      };

    let result: ApiResult<CherryHubAdminApi.PlatformApplication> | null = null;

    if (props.application == null) {
      result = await props.apiClient.createPlatformApplication(applicationDoc);
    } else {
      result = await props.apiClient.updatePlatformApplication(
        props.application.id,
        applicationDoc
      );
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validate: (values) => {
    let errors: FormikErrors<ApplicationDetailFormValues> = {};

    return errors;
  },
  validationSchema: Yup.object({
    supportEmailAddress: Yup.string().email().notRequired(),
    publisherPrimaryContactEmailAddress: Yup.string().email().notRequired(),
    publisherSecondaryContactEmailAddress: Yup.string().email().notRequired(),
  }),
})(InnerForm);
